// HttpTransactionView.js — Reusable HTTP request/response viewer
// Used by: DriftAlarmDrawer, AssetDataSections (vuln details), ScrutinizeResults, ScanResults, FindingsTab

import React, { useState, useMemo, useCallback } from 'react';
import './HttpTransactionView.css';

const SENSITIVE_HEADERS = [
    'authorization',
    'cookie',
    'set-cookie',
    'x-api-key',
    'x-auth-token',
    'proxy-authorization'
];

const BODY_TRUNCATE_DEFAULT = 5000;
const BODY_TRUNCATE_EXPANDED = 200 * 1024;

/**
 * Parse raw finding data into a normalized httpTransaction shape.
 */
export function parseHttpTransactionFromRaw(raw, matched) {
    if (!raw) return null;
    const hasRequest = !!raw.request;
    const hasResponse = !!raw.response;
    const hasCurl = !!raw['curl-command'];
    if (!hasRequest && !hasResponse && !hasCurl) return null;

    return {
        request: hasRequest ? {
            raw: raw.request,
            method: raw.request?.match(/^(\w+)\s/)?.[1] || 'GET',
            url: raw.url || matched || ''
        } : null,
        response: hasResponse ? {
            raw: raw.response,
            status: raw.response?.match(/HTTP\/\d\.\d\s+(\d+)/)?.[1] || '',
            headers: raw.response?.split('\r\n\r\n')?.[0] || '',
            body: raw.response?.split('\r\n\r\n')?.[1] || ''
        } : null,
        curlCommand: raw['curl-command'] || null
    };
}

/**
 * Redact sensitive header VALUES in a header block (or raw HTTP message).
 * Only operates on the header section — stops at the first blank line so that
 * a response body containing header-like text is never over-stripped.
 */
export function redactSensitiveHeaders(text) {
    if (!text) return text;
    const lines = text.split('\n');
    let inBody = false;
    const out = lines.map(line => {
        if (inBody) return line;
        if (line.trim() === '') { inBody = true; return line; }
        const colonIdx = line.indexOf(':');
        if (colonIdx === -1) return line;
        const name = line.slice(0, colonIdx).trim().toLowerCase();
        if (SENSITIVE_HEADERS.includes(name)) {
            // Preserve original \r at end-of-line if present
            const trailingCR = line.endsWith('\r') ? '\r' : '';
            return `${line.slice(0, colonIdx + 1)} [REDACTED]${trailingCR}`;
        }
        return line;
    });
    return out.join('\n');
}

/**
 * Map a numeric HTTP status to a class suffix. Non-numeric → ''.
 */
export function classifyStatus(status) {
    const n = parseInt(status, 10);
    if (!Number.isFinite(n) || n < 100 || n > 599) return '';
    if (n >= 500) return '5xx';
    if (n >= 400) return '4xx';
    if (n >= 300) return '3xx';
    if (n >= 200) return '2xx';
    return '';
}

/**
 * Format a byte count as B / KB / MB.
 */
export function formatByteSize(bytes) {
    if (!Number.isFinite(bytes) || bytes < 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Try to parse + pretty-print as JSON. Returns the formatted string or null on failure.
 */
export function tryPrettyJson(body) {
    if (!body || typeof body !== 'string') return null;
    const trimmed = body.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
    try {
        return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch (_e) {
        return null;
    }
}

/**
 * Render a body string with optional highlight of `payload` occurrences.
 * Returns an array of React nodes safe to drop inside <pre>.
 */
function renderWithHighlight(text, payload) {
    if (!payload || typeof payload !== 'string' || !text.includes(payload)) {
        return text;
    }
    const parts = text.split(payload);
    const nodes = [];
    parts.forEach((part, i) => {
        if (i > 0) {
            nodes.push(
                <mark key={`m-${i}`} className="http-transaction-view__highlight">
                    {payload}
                </mark>
            );
        }
        nodes.push(<React.Fragment key={`t-${i}`}>{part}</React.Fragment>);
    });
    return nodes;
}

function CopyButton({ targetText, copyKey, copiedKey, onCopy }) {
    const isCopied = copiedKey === copyKey;
    const handle = (e) => {
        e.stopPropagation();
        if (!targetText) return;
        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(targetText).then(() => onCopy(copyKey));
        }
    };
    return (
        <button
            type="button"
            className={`http-transaction-view__copy${isCopied ? ' http-transaction-view__copy--ack' : ''}`}
            onClick={handle}
            aria-label={isCopied ? 'Copied' : 'Copy to clipboard'}
        >
            {isCopied ? 'Copied' : 'Copy'}
        </button>
    );
}

/**
 * @param {object} props
 * @param {object} [props.httpTransaction] - { request, response, curlCommand }
 * @param {string} [props.className] - Optional additional CSS class
 * @param {string} [props.matcherPayload] - Optional reflected payload to highlight in response body
 * @param {boolean} [props.showEmpty] - If set, render a placeholder when transaction is missing
 */
function HttpTransactionView({ httpTransaction, className, matcherPayload, showEmpty }) {
    const [expanded, setExpanded] = useState({});
    const [bodyExpanded, setBodyExpanded] = useState(false);
    const [copiedKey, setCopiedKey] = useState(null);

    const { request, response, curlCommand } = httpTransaction || {};

    // Headers + raw redacted at render time (originals preserved in source object)
    const redactedRequestRaw = useMemo(
        () => redactSensitiveHeaders(request?.raw),
        [request?.raw]
    );
    const redactedResponseHeaders = useMemo(
        () => redactSensitiveHeaders(response?.headers),
        [response?.headers]
    );

    // Body pretty-print + truncation
    const bodyState = useMemo(() => {
        const raw = response?.body || '';
        const pretty = tryPrettyJson(raw);
        const display = pretty != null ? pretty : raw;
        const isJson = pretty != null;
        const originalBytes = raw.length;
        return { display, isJson, originalBytes };
    }, [response?.body]);

    const displayBody = useMemo(() => {
        const limit = bodyExpanded ? BODY_TRUNCATE_EXPANDED : BODY_TRUNCATE_DEFAULT;
        const truncated = bodyState.display.length > limit;
        const slice = bodyState.display.substring(0, limit);
        return { text: slice, truncated };
    }, [bodyState.display, bodyExpanded]);

    const hasSensitiveInRequest = redactedRequestRaw !== request?.raw;
    const hasSensitiveInResponse = redactedResponseHeaders !== response?.headers;

    const toggle = useCallback((section) => {
        setExpanded(prev => ({ ...prev, [section]: !prev[section] }));
    }, []);

    const onCopy = useCallback((key) => {
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(k => (k === key ? null : k)), 1000);
    }, []);

    if (!httpTransaction) {
        if (showEmpty) {
            return (
                <div
                    className={`http-transaction-view http-transaction-view--empty ${className || ''}`}
                    data-testid="http-transaction-view"
                >
                    <div className="http-transaction-view__empty-line">
                        HTTP transaction not captured for this scan.
                    </div>
                </div>
            );
        }
        return null;
    }

    const statusClass = response?.status ? classifyStatus(response.status) : '';

    return (
        <div className={`http-transaction-view ${className || ''}`} data-testid="http-transaction-view">
            <div className="http-transaction-view__header">HTTP Transaction</div>

            {/* Request */}
            {request && (
                <div className="http-transaction-view__section">
                    <div
                        className="http-transaction-view__section-header"
                        onClick={() => toggle('request')}
                    >
                        <span>Request {request.method && request.url ? `(${request.method} ${request.url})` : ''}</span>
                        <span className="http-transaction-view__expand">{expanded.request ? '▼' : '▶'}</span>
                    </div>
                    {expanded.request && (
                        <div className="http-transaction-view__content">
                            {request.method && (
                                <div className="http-transaction-view__row">
                                    <span className="http-transaction-view__label">Method:</span>
                                    <span className="http-transaction-view__value">{request.method}</span>
                                </div>
                            )}
                            {request.url && (
                                <div className="http-transaction-view__row">
                                    <span className="http-transaction-view__label">URL:</span>
                                    <span className="http-transaction-view__value mono">{request.url}</span>
                                </div>
                            )}
                            {request.raw && (
                                <div className="http-transaction-view__raw">
                                    <div className="http-transaction-view__raw-toolbar">
                                        <CopyButton
                                            targetText={request.raw}
                                            copyKey="request"
                                            copiedKey={copiedKey}
                                            onCopy={onCopy}
                                        />
                                    </div>
                                    <pre>{redactedRequestRaw}</pre>
                                    {hasSensitiveInRequest && (
                                        <div className="http-transaction-view__redaction-note">
                                            Sensitive headers redacted in display. Full data preserved in scan blob.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Response */}
            {response && (
                <div className="http-transaction-view__section">
                    <div
                        className="http-transaction-view__section-header"
                        onClick={() => toggle('response')}
                    >
                        <span>
                            Response{' '}
                            {response.status ? (
                                <span
                                    className={`http-transaction-view__status${statusClass ? ` http-transaction-view__status--${statusClass}` : ''}`}
                                >
                                    {response.status}
                                </span>
                            ) : ''}
                        </span>
                        <span className="http-transaction-view__expand">{expanded.response ? '▼' : '▶'}</span>
                    </div>
                    {expanded.response && (
                        <div className="http-transaction-view__content">
                            {response.status && (
                                <div className="http-transaction-view__row">
                                    <span className="http-transaction-view__label">Status:</span>
                                    <span
                                        className={`http-transaction-view__value http-transaction-view__status-chip${statusClass ? ` http-transaction-view__status-chip--${statusClass}` : ''}`}
                                    >
                                        {response.status}
                                    </span>
                                </div>
                            )}
                            {response.headers && (
                                <div className="http-transaction-view__raw">
                                    <div className="http-transaction-view__raw-label">
                                        Headers:
                                        <CopyButton
                                            targetText={response.headers}
                                            copyKey="response-headers"
                                            copiedKey={copiedKey}
                                            onCopy={onCopy}
                                        />
                                    </div>
                                    <pre>{redactedResponseHeaders}</pre>
                                    {hasSensitiveInResponse && (
                                        <div className="http-transaction-view__redaction-note">
                                            Sensitive headers redacted in display. Full data preserved in scan blob.
                                        </div>
                                    )}
                                </div>
                            )}
                            {response.body && (
                                <div className="http-transaction-view__raw">
                                    <div className="http-transaction-view__raw-label">
                                        Body:
                                        <span className="http-transaction-view__size-badge">
                                            {formatByteSize(bodyState.originalBytes)}
                                        </span>
                                        {bodyState.isJson && (
                                            <span className="http-transaction-view__json-badge">JSON</span>
                                        )}
                                        <CopyButton
                                            targetText={response.body}
                                            copyKey="response-body"
                                            copiedKey={copiedKey}
                                            onCopy={onCopy}
                                        />
                                    </div>
                                    <pre>{renderWithHighlight(displayBody.text, matcherPayload)}</pre>
                                    {displayBody.truncated && !bodyExpanded && (
                                        <button
                                            type="button"
                                            className="http-transaction-view__toggle-body"
                                            onClick={() => setBodyExpanded(true)}
                                        >
                                            Show full body (up to 200 KB)
                                        </button>
                                    )}
                                    {bodyExpanded && bodyState.display.length > BODY_TRUNCATE_EXPANDED && (
                                        <div className="http-transaction-view__truncated-note">
                                            Truncated at 200 KB for display. Full data preserved in scan blob.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* cURL */}
            {curlCommand && (
                <div className="http-transaction-view__section">
                    <div
                        className="http-transaction-view__section-header"
                        onClick={() => toggle('curl')}
                    >
                        <span>Reproduce with cURL</span>
                        <span className="http-transaction-view__expand">{expanded.curl ? '▼' : '▶'}</span>
                    </div>
                    {expanded.curl && (
                        <div className="http-transaction-view__content">
                            <div className="http-transaction-view__raw-toolbar">
                                <CopyButton
                                    targetText={curlCommand}
                                    copyKey="curl"
                                    copiedKey={copiedKey}
                                    onCopy={onCopy}
                                />
                            </div>
                            <pre className="http-transaction-view__curl">{curlCommand}</pre>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default HttpTransactionView;
